import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import ImmeubleForm from "./components/ImmeubleForm";
import { useImmeubles } from "./ImmeublesProvider";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import type { Immeuble } from "./types";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

export default function ImmeublesPage() {
  const { immeubles, removeImmeuble } = useImmeubles();
  const { stats: bienStats, countByImmeuble: countBiensByImmeuble } = useBiens();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Immeuble | null>(null);
  const [toDelete, setToDelete] = useState<Immeuble | null>(null);
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const { regionNames } = useTerritory();
  const regionFilterOptions = useMemo(
    () => [
      { value: "", label: "Toutes les régions" },
      ...regionNames.map((name) => ({ value: name, label: name })),
    ],
    [regionNames]
  );

  const hasImmeubles = immeubles.length > 0;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = immeubles.filter((immeuble) => {
      const matchesRegion = !region || immeuble.region === region;
      const matchesSearch =
        !term ||
        [immeuble.nom, immeuble.commune, immeuble.province, immeuble.secteur, immeuble.adresse]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesRegion && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "nom") return a.nom.localeCompare(b.nom, "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [immeubles, search, region, sort]);

  const isFiltering = Boolean(search.trim()) || Boolean(region);

  const exportColumns: ExportColumn<Immeuble>[] = [
    { header: "Nom", value: (immeuble) => immeuble.nom },
    { header: "Région", value: (immeuble) => immeuble.region },
    { header: "Province", value: (immeuble) => immeuble.province },
    { header: "Commune", value: (immeuble) => immeuble.commune },
    { header: "Secteur / Quartier", value: (immeuble) => immeuble.secteur },
    { header: "Niveaux", value: (immeuble) => immeuble.niveaux },
    { header: "Adresse", value: (immeuble) => immeuble.adresse },
    { header: "Créé le", value: (immeuble) => formatDate(immeuble.createdAt) },
  ];

  const columns: DataTableColumn<Immeuble>[] = [
    {
      key: "immeuble",
      header: "Immeuble",
      className: "min-w-[220px]",
      render: (immeuble) => (
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-100 text-foreground-300">
            {immeuble.photo ? (
              <img
                src={immeuble.photo}
                alt={immeuble.nom}
                title={`${immeuble.nom} — immeuble à ${immeuble.commune}`}
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <i className="ri-building-2-line text-lg" aria-hidden="true" />
            )}
          </span>
          <span className="truncate font-label font-medium text-foreground-950">{immeuble.nom}</span>
        </div>
      ),
    },
    {
      key: "localisation",
      header: "Localisation",
      className: "min-w-[220px]",
      render: (immeuble) => (
        <span className="text-foreground-700">
          {[immeuble.commune, immeuble.province, immeuble.region].filter(Boolean).join(" · ") || "Non renseignée"}
        </span>
      ),
    },
    {
      key: "niveaux",
      header: "Niveaux",
      align: "center",
      render: (immeuble) => (
        <span className="text-foreground-700">{`${immeuble.niveaux} niveau${immeuble.niveaux > 1 ? "x" : ""}`}</span>
      ),
    },
    {
      key: "biens",
      header: "Biens",
      align: "center",
      render: (immeuble) => (
        <span className="font-label font-medium text-foreground-950">{countBiensByImmeuble(immeuble.id)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[210px]",
      render: (immeuble) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/proprietaire/immeubles/${immeuble.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions onEdit={() => setEditing(immeuble)} onDelete={() => setToDelete(immeuble)} />
        </div>
      ),
    },
  ];

  const handleAdd = () => setFormOpen(true);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Immeubles</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Créez et organisez vos immeubles par région, province, commune et secteur. Chaque immeuble regroupe
            ensuite ses biens, locataires, contrats et finances.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu
            rows={filtered}
            columns={exportColumns}
            filename="immeubles"
            title="Immeubles"
            subtitle="Données actuellement affichées"
          />
          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter un immeuble
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-building-2-line" label="Immeubles" value={String(immeubles.length)} tone="primary" />
        <StatCard icon="ri-home-4-line" label="Biens" value={String(bienStats.total)} tone="secondary" />
        <StatCard icon="ri-home-smile-2-line" label="Biens occupés" value={String(bienStats.occupes)} tone="accent" />
        <StatCard icon="ri-home-8-line" label="Biens disponibles" value={String(bienStats.disponibles)} tone="neutral" />
      </div>

      <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="immeuble-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="immeuble-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nom, commune, secteur, adresse..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="immeuble-region-filter" className="text-xs font-label font-medium text-foreground-700">
                Région
              </label>
              <Select
                id="immeuble-region-filter"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                options={regionFilterOptions}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between lg:justify-end">
            <span className="text-xs text-foreground-500 lg:hidden">
              {filtered.length} immeuble{filtered.length > 1 ? "s" : ""}
            </span>
            <div className="flex flex-col gap-1.5 sm:w-48">
              <label htmlFor="immeuble-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="immeuble-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-background-200 pt-3">
          <p className="text-xs text-foreground-500">
            <span className="hidden lg:inline">
              {filtered.length} immeuble{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
              {hasImmeubles ? ` sur ${immeubles.length}` : ""}
            </span>
            <span className="lg:hidden">
              Affichage : {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
            </span>
          </p>
        </div>
      </section>

      {!hasImmeubles && (
        <EmptyState
          icon="ri-building-2-line"
          title="Aucun immeuble pour le moment"
          description="Commencez par ajouter votre premier immeuble avec sa localisation complète. Vous pourrez ensuite y rattacher vos biens, locataires et contrats."
          action={
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un immeuble
            </button>
          }
        />
      )}

      {hasImmeubles && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun immeuble ne correspond à votre recherche ou au filtre sélectionné. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setRegion("");
              }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(immeuble) => immeuble.id}
          minWidthClass="min-w-[900px]"
          caption="Immeubles"
        />
      )}

      {isFiltering && filtered.length > 0 && (
        <p className="text-center text-xs text-foreground-400">
          Filtres actifs sur {immeubles.length} immeuble{immeubles.length > 1 ? "s" : ""}.
        </p>
      )}

      <ImmeubleForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeImmeuble(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer l'immeuble"
        description={toDelete ? `Voulez-vous vraiment supprimer « ${toDelete.nom} » ? Cette action est définitive.` : ""}
        blockedMessage={
          toDelete && countBiensByImmeuble(toDelete.id) > 0
            ? `Cet immeuble contient ${countBiensByImmeuble(toDelete.id)} bien(s) rattaché(s). Supprimez ou déplacez d'abord ses biens pour éviter des données orphelines.`
            : undefined
        }
      />
    </div>
  );
}